import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { SiteHeader } from "../components/SiteHeader";
import { MapPin, CalendarPlus, Share2, X, ExternalLink, Check, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";

const imgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);
const todayStr = () => new Date().toISOString().slice(0, 10);

const fmtDate = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};
const timeRange = (ev) => [ev.start_time, ev.end_time].filter(Boolean).join(" – ");
const mapsLink = (ev) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([ev.venue_name, ev.venue_address].filter(Boolean).join(", "))}`;

const parseTime = (t) => {
  if (!t) return null;
  const m = t.toLowerCase().match(/(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?/);
  if (!m) return null;
  let h = parseInt(m[1], 10); const min = m[2] ? parseInt(m[2], 10) : 0;
  const ap = (m[3] || "").replace(/\./g, "");
  if (ap === "pm" && h < 12) h += 12; if (ap === "am" && h === 12) h = 0;
  return String(h).padStart(2, "0") + String(min).padStart(2, "0") + "00";
};
const esc = (s) => (s || "").replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");

const downloadIcs = (ev) => {
  const d = (ev.date || "").replace(/-/g, "");
  const st = parseTime(ev.start_time); const et = parseTime(ev.end_time);
  let dtStart, dtEnd;
  if (d && st) { dtStart = `DTSTART:${d}T${st}`; dtEnd = `DTEND:${d}T${et || st}`; }
  else if (d) {
    dtStart = `DTSTART;VALUE=DATE:${d}`;
    const nd = new Date(Number(d.slice(0, 4)), Number(d.slice(4, 6)) - 1, Number(d.slice(6, 8)) + 1);
    dtEnd = `DTEND;VALUE=DATE:${nd.toISOString().slice(0, 10).replace(/-/g, "")}`;
  }
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Braced No More//Telegram//EN", "BEGIN:VEVENT",
    `UID:${ev.id}@bracednomore`, `SUMMARY:${esc(ev.title)}`, dtStart, dtEnd,
    `LOCATION:${esc([ev.venue_name, ev.venue_address].filter(Boolean).join(", "))}`,
    `DESCRIPTION:${esc(ev.description || "")}`, "END:VEVENT", "END:VCALENDAR"].filter(Boolean);
  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${(ev.title || "event").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
};

const EventCard = ({ ev, onZoom }) => {
  const [copied, setCopied] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [remindEmail, setRemindEmail] = useState("");
  const [remindName, setRemindName] = useState("");
  const [remindBusy, setRemindBusy] = useState(false);
  const [remindDone, setRemindDone] = useState(false);
  const share = async () => {
    const url = `${window.location.origin}/telegram#event-${ev.id}`;
    try { await navigator.clipboard.writeText(url); } catch (e) { /* ignore */ }
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };
  const submitRemind = async (e) => {
    e.preventDefault();
    const email = remindEmail.trim();
    if (!email || !email.includes("@")) { toast.error("Please enter a valid email."); return; }
    setRemindBusy(true);
    try {
      await axios.post(`${API}/telegram/events/${ev.id}/remind`, { email, name: remindName.trim() });
      setRemindDone(true);
      toast.success("You're on the list — we'll email you the day before.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set the reminder. Please try again.");
    } finally { setRemindBusy(false); }
  };
  return (
    <article id={`event-${ev.id}`} data-testid={`telegram-card-${ev.id}`}
      className="overflow-hidden rounded-2xl bg-ivory-50 ring-1 ring-ivory-400/60 shadow-[0_24px_60px_-40px_rgba(60,46,30,0.5)]">
      {ev.flyer_url && (
        <button onClick={() => onZoom(ev.flyer_url)} data-testid={`telegram-flyer-${ev.id}`} className="block w-full">
          <img src={imgSrc(ev.flyer_url)} alt={ev.title} className="max-h-[520px] w-full object-cover transition hover:opacity-95" />
        </button>
      )}
      <div className="p-6 sm:p-7">
        {ev.featured && <span className="mb-2 inline-block rounded-full bg-gold/15 px-3 py-1 text-[11px] uppercase tracking-wide text-gold-deep">Featured</span>}
        <h2 className="font-display text-2xl text-ink">{ev.title}</h2>
        {ev.subtitle && <p className="mt-1 text-sm italic text-ink-soft">{ev.subtitle}</p>}
        <p className="mt-3 font-display text-ink">{fmtDate(ev.date)}{timeRange(ev) ? ` · ${timeRange(ev)}` : ""}</p>
        {(ev.venue_name || ev.venue_address) && (
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-ink-soft">
            <MapPin className="h-4 w-4 text-gold-deep" /> {[ev.venue_name, ev.venue_address].filter(Boolean).join(", ")}
            <a href={mapsLink(ev)} target="_blank" rel="noopener noreferrer" data-testid={`telegram-directions-${ev.id}`} className="text-gold-deep underline underline-offset-2">Get directions</a>
          </p>
        )}
        {ev.host && <p className="mt-1 text-sm text-ink-muted">Hosted with {ev.host}</p>}
        {ev.cost && <p className="mt-1 text-sm text-ink-muted">Cost: {ev.cost}</p>}
        {ev.who_for && <p className="mt-1 text-sm text-ink-muted">For: {ev.who_for}</p>}
        {ev.description && <p className="mt-4 whitespace-pre-wrap text-[15px] leading-relaxed text-ink-soft">{ev.description}</p>}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {ev.signup_url && (
            <a href={ev.signup_url} target="_blank" rel="noopener noreferrer" data-testid={`telegram-signup-${ev.id}`}
              className="debossed-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-ink">
              {ev.signup_label || "Save your seat"}
            </a>
          )}
          <button onClick={() => downloadIcs(ev)} data-testid={`telegram-ics-${ev.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-4 py-2 text-sm text-ink-soft transition hover:border-gold/50">
            <CalendarPlus className="h-4 w-4" /> Add to calendar
          </button>
          <button onClick={() => setRemindOpen((v) => !v)} data-testid={`telegram-remind-toggle-${ev.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-4 py-2 text-sm text-ink-soft transition hover:border-gold/50">
            <Bell className="h-4 w-4" /> Remind me
          </button>
          <button onClick={share} data-testid={`telegram-share-${ev.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-4 py-2 text-sm text-ink-soft transition hover:border-gold/50">
            {copied ? <><Check className="h-4 w-4 text-sage" /> Copied</> : <><Share2 className="h-4 w-4" /> Share</>}
          </button>
          {ev.source_url && <a href={ev.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-gold-deep"><ExternalLink className="h-3.5 w-3.5" /> Source</a>}
        </div>
        {remindOpen && (
          remindDone ? (
            <div data-testid={`telegram-remind-done-${ev.id}`} className="mt-4 flex items-center gap-2 rounded-xl bg-sage/15 px-4 py-3 text-sm text-ink">
              <Check className="h-4 w-4 text-sage" /> We'll send a gentle reminder the day before. Take care.
            </div>
          ) : (
            <form onSubmit={submitRemind} data-testid={`telegram-remind-form-${ev.id}`} className="mt-4 rounded-xl bg-ivory-100 p-4 ring-1 ring-ivory-400/60">
              <p className="text-sm text-ink-soft">Get a gentle email reminder the morning before this event.</p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <input value={remindName} onChange={(e) => setRemindName(e.target.value)} data-testid={`telegram-remind-name-${ev.id}`} placeholder="Your name (optional)" className="w-full rounded-full border border-ink/15 bg-white px-4 py-2 text-sm text-ink outline-none focus:border-gold/60" />
                <input value={remindEmail} onChange={(e) => setRemindEmail(e.target.value)} type="email" required data-testid={`telegram-remind-email-${ev.id}`} placeholder="you@email.com" className="w-full rounded-full border border-ink/15 bg-white px-4 py-2 text-sm text-ink outline-none focus:border-gold/60" />
                <button type="submit" disabled={remindBusy} data-testid={`telegram-remind-submit-${ev.id}`} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-ink px-5 py-2 text-sm font-display text-ivory-50 disabled:opacity-50">
                  {remindBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} Remind me
                </button>
              </div>
            </form>
          )
        )}
      </div>
    </article>
  );
};

export default function Telegram() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    axios.get(`${API}/telegram/events`).then(({ data }) => setEvents(data.events || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const { upcoming, past } = useMemo(() => {
    const t = todayStr();
    const up = events.filter((e) => (e.date || "") >= t).sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const pa = events.filter((e) => (e.date || "") < t).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    up.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || (a.date || "").localeCompare(b.date || ""));
    return { upcoming: up, past: pa };
  }, [events]);

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="telegram-page">
      <SiteHeader theme="light" />
      <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8">
        <p className="kicker text-gold-foil">The Telegram</p>
        <h1 className="mt-2 font-display text-4xl text-ink sm:text-5xl">Where to find Casey next.</h1>
        <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-ink-soft">Workshops, talks and gatherings — in person and online. Save a seat, add it to your calendar, or share it with someone who needs it.</p>

        {loading && <p className="mt-10 text-sm text-ink-muted">Loading…</p>}
        {!loading && upcoming.length === 0 && past.length === 0 && (
          <p className="mt-10 text-sm text-ink-muted" data-testid="telegram-empty">No events on the board yet — check back soon.</p>
        )}

        {upcoming.length > 0 && (
          <div className="mt-10 space-y-8" data-testid="telegram-upcoming">
            {upcoming.map((ev) => <EventCard key={ev.id} ev={ev} onZoom={setZoom} />)}
          </div>
        )}

        {past.length > 0 && (
          <div className="mt-14" data-testid="telegram-recently">
            <h2 className="font-display text-2xl text-ink">Recently</h2>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {past.map((ev) => (
                <button key={ev.id} onClick={() => ev.flyer_url && setZoom(ev.flyer_url)} data-testid={`telegram-past-${ev.id}`} className="group text-left">
                  {ev.flyer_url && <img src={imgSrc(ev.flyer_url)} alt={ev.title} className="aspect-[3/4] w-full rounded-lg object-cover opacity-80 ring-1 ring-ivory-400/60 transition group-hover:opacity-100" />}
                  <p className="mt-2 font-display text-sm leading-tight text-ink-soft">{ev.title}</p>
                  <p className="text-xs text-ink-muted">{fmtDate(ev.date)}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {zoom && (
        <div onClick={() => setZoom(null)} data-testid="telegram-lightbox" className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/80 p-6 backdrop-blur-sm">
          <button className="absolute right-5 top-5 text-ivory-50" aria-label="Close"><X className="h-6 w-6" /></button>
          <img src={imgSrc(zoom)} alt="Flyer" className="max-h-[90vh] max-w-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}
